import React, { useState } from 'react';
import styles from './styles.module.css';
import { FertilizerWarehouseResponse } from '../api/types';
import LeasingSelectYear from '../../../mechanization/leasing/Leasingselect';

interface DinamicbyRegionsProps {
  fertilizersWarehouseData: FertilizerWarehouseResponse | undefined;
  selectedYear: number | null;
  onYearChange: (year: number | null) => void;
}

interface TooltipState {
  x: number;
  y: number;
  region: string;
  values: {
    nitrogen: number;
    phosphorus: number;
    potassium: number;
  };
}

export const DinamicByRegions: React.FC<DinamicbyRegionsProps> = ({ 
  fertilizersWarehouseData,
  selectedYear,
  onYearChange
}) => {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  if (!fertilizersWarehouseData?.res?.regionFertilizers) {
    return null;
  }

  const regionFertilizers = fertilizersWarehouseData.res.regionFertilizers;

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>, region: string) => {
    const regionData = regionFertilizers.find(r => r.regionNameRu === region);
    if (!regionData) return;

    setTooltip({
      x: e.clientX,
      y: e.clientY,
      region,
      values: {
        nitrogen: regionData.nitrogenAmount,
        phosphorus: regionData.phosphorusAmount,
        potassium: regionData.potassiumAmount
      }
    });
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  const maxValues = regionFertilizers.reduce((acc, region) => ({
    nitrogen: Math.max(acc.nitrogen, region.nitrogenAmount),
    phosphorus: Math.max(acc.phosphorus, region.phosphorusAmount),
    potassium: Math.max(acc.potassium, region.potassiumAmount)
  }), { nitrogen: 0, phosphorus: 0, potassium: 0 });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Диаграмма объемов удобрений по регионам</h2>
        <div className={styles.yearSelect}>
          <LeasingSelectYear value={selectedYear} onChange={onYearChange} />
        </div>
      </div>
      <div className={styles.chart}>
        {regionFertilizers.map((region) => (
          <div
            key={region.regionNameRu}
            className={styles.region}
            onMouseEnter={(e) => handleMouseEnter(e, region.regionNameRu)}
            onMouseLeave={handleMouseLeave}
          >
            <div className={styles.bars}>
              <div
                className={styles.bar}
                style={{
                  height: `${(region.nitrogenAmount / maxValues.nitrogen) * 100}%`,
                  backgroundColor: '#4D8E21'
                }}
              />
              <div
                className={styles.bar}
                style={{
                  height: `${(region.phosphorusAmount / maxValues.phosphorus) * 100}%`,
                  backgroundColor: '#B6D434'
                }}
              />
              <div
                className={styles.bar}
                style={{
                  height: `${(region.potassiumAmount / maxValues.potassium) * 100}%`,
                  backgroundColor: '#FFC700'
                }}
              />
            </div>
            <div className={styles.regionName}>{region.regionNameRu}</div>
          </div>
        ))}
      </div>

      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: '#4D8E21' }} />
          <span>Азот</span>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: '#B6D434' }} />
          <span>Фосфор</span>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: '#FFC700' }} />
          <span>Калий</span>
        </div>
      </div>

      {tooltip && (
        <div
          className={styles.tooltip}
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          <h3>{tooltip.region}</h3>
          <p>Азот: {tooltip.values.nitrogen} кг</p>
          <p>Фосфор: {tooltip.values.phosphorus} кг</p>
          <p>Калий: {tooltip.values.potassium} кг</p>
        </div>
      )}
    </div>
  );
};